import React, {useState, useEffect, useRef} from 'react';
import {Grid, TextField, Link, InputAdornment, Tooltip} from '@material-ui/core';
import {Types} from "../index";
import API from "@beardeddevops/react.api"
import {LineChart} from "react-chartkick";
import 'chart.js'
import moment from "moment-timezone";
import Card from "@material-ui/core/Card";


function getStartOfWeek(date) {
	const currentDate = moment(date);
	return currentDate.isoWeekday() === 7 // Check if the date is already Sunday
		? currentDate // Add 6 days to reach Saturday
		: currentDate.isoWeekday(0); // Otherwise, move to the nearest Saturday
}

function getEndOfWeek(date) {
	const currentDate = moment(date);
	return currentDate.isoWeekday() === 7 // Check if the date is already Sunday
		? currentDate.add(6, "days") // Add 6 days to reach Saturday
		: currentDate.isoWeekday(6); // Otherwise, move to the nearest Saturday
}


const ExecutiveCharts = ({selectedUsers, filterPeriod, forceRefresh, lastTriggered}) => {
	const [data, setData] = useState([])

	async function formatTorsData(torsData, startDate, endDate) {
		const groupedData = {};

		if (filterPeriod === "7 days") {
			const currentDate = moment(startDate);
			while (currentDate.isSameOrBefore(moment(endDate), "day")) {
				const dateStr = currentDate.format("DD/MM/YY");
				Object.keys(torsData).forEach((torType) => {
					if (!groupedData[torType]) {
						groupedData[torType] = {};
					}
					groupedData[torType][dateStr] = 0;
				});
				currentDate.add(1, "day");
			}

			Object.entries(torsData).forEach(([torType, torEntries]) => {
				torEntries.forEach(([rawDate, value]) => {
					const date = moment(rawDate).format("DD/MM/YY");
					if (groupedData[torType][date] !== undefined) {
						groupedData[torType][date] += value;
					}
				});
			});
		} else {
			const currentWeek = getStartOfWeek(startDate);
			const endWeek = getEndOfWeek(endDate);

			while (currentWeek.isSameOrBefore(endWeek, "day")) {
				const weekStr = `Week of ${currentWeek.format("DD/MM/YY")}`;
				Object.keys(torsData).forEach((torType) => {
					if (!groupedData[torType]) {
						groupedData[torType] = {};
					}
					groupedData[torType][weekStr] = 0;
				});
				currentWeek.add(1, "week");
			}

			Object.entries(torsData).forEach(([torType, torEntries]) => {
				torEntries.forEach(([rawDate, value]) => {
					const date = moment(rawDate).format("YYYY-MM-DD");
					const startOfWeek = getStartOfWeek(date).format("DD/MM/YY");
					const weekStr = `Week of ${startOfWeek}`;

					if (groupedData[torType][weekStr] !== undefined) {
						groupedData[torType][weekStr] += value;
					}
				});
			});
		}

		// Convert grouped data to chart format
		return Object.entries(groupedData).map(([torType, data]) => ({
			name: torType,
			data: Object.entries(data),
		}));
	}


	async function formatData(data, startDate, endDate, wos = false) {
		//console.log("Input data to formatData:", data);
		//console.log("Filter period:", filterPeriod);

		const groupedData = {};

		if (filterPeriod === "7 days") {
			const currentDate = moment(startDate);
			while (currentDate.isSameOrBefore(moment(endDate), "day")) {
				const dateStr = currentDate.format("DD/MM/YY");
				groupedData[dateStr] = 0;
				currentDate.add(1, "day");
			}

			data.forEach(entry => {
				const [rawDate, value] = Object.entries(entry)[0];
				const date = moment(rawDate).format("DD/MM/YY");
				//console.log("Processing entry:", { date, value });

				// Convert value to float and round to 2 decimal places
				const parsedValue = parseFloat(value);
				if (groupedData[date] !== undefined) {
					groupedData[date] = parseFloat((groupedData[date] + parsedValue).toFixed(2)); // Rounding to 2 decimal places
				}
			});
		} else {
			const currentWeek = getStartOfWeek(startDate);
			const endWeek = getEndOfWeek(endDate);
			//console.log(startDate, currentWeek, endDate, endWeek)

			while (currentWeek.isSameOrBefore(endWeek, "day")) {
				const weekStr = `Week of ${currentWeek.format("DD/MM/YY")}`;
				groupedData[weekStr] = 0;
				currentWeek.add(1, "week");
			}

			//console.log('GROUPED BEFORE', groupedData)

			data.forEach(entry => {
				const [rawDate, value] = Object.entries(entry)[0];
				const date = moment(rawDate).format("YYYY-MM-DD");

				// Get the start of the week
				const startOfWeek = getStartOfWeek(date).format("DD/MM/YY");

				// Create the label as "Week of DD/MM/YY"
				const weekStr = `Week of ${startOfWeek}`;

				// Convert value to float and round to 2 decimal places
				const parsedValue = parseFloat(value);
				if (groupedData[weekStr] !== undefined) {
					groupedData[weekStr] = parseFloat((groupedData[weekStr] + parsedValue).toFixed(2)); // Rounding to 2 decimal places
				}
			});
		}

		//console.log("grouped", groupedData)

		// Convert grouped data to chart format
		return Object.entries(groupedData).map(([key, value]) => {
			if (wos) {
				return [key, (value / (wos.filter((wo) => wo[0] === key)[0][1] > 0 ? wos.filter((wo) => wo[0] === key)[0][1] : 1))];
			} else {
				return [key, value];
			}
		});
	}


	useEffect(() => {
		if (selectedUsers.length === 0) {
			setData([])
			return;
		}
		fetchData().then((chartData) => {
			if (chartData?.length > 0) {
				setData(chartData)
			} else {
				setData([])
			}
		})
	}, [filterPeriod, selectedUsers.length, forceRefresh, lastTriggered]);

	async function fetchData() {
		const endDate = moment().format('YYYY-MM-DD 23:59:59');
		const startDate = moment().subtract(filterPeriod.split(' ')[0], filterPeriod.split(' ')[1]).format('YYYY-MM-DD 00:00:00')
		let model = new API.BuildClass(Types.Executives._name, API.RequestType.READ);
		model.filter = {
			'filterEngineers': selectedUsers,
			'filterDateStart': startDate,
			'filterDateEnd': endDate
		}
		const data = await model.submit(null, 'GET_ALL');
		return await Promise.all(
			data.map(async ({user_data, total_numbers, feedbacks, refunds, refunds_dollars, revisits, work_orders, work_order_dollars, revenue, tors}) => {
				const wos = await formatData(work_orders, startDate, endDate);
				return ({
					user_data,
					total_numbers,
					feedbacks: await formatData(feedbacks, startDate, endDate),
					refunds: await formatData(refunds, startDate, endDate),
					refunds_dollars: await formatData(refunds_dollars, startDate, endDate),
					revisits: await formatData(revisits, startDate, endDate),
					work_orders: wos,
					work_order_dollars: await formatData(work_order_dollars, startDate, endDate, wos),
					revenue: await formatData(revenue, startDate, endDate),
					tors: await formatTorsData(tors, startDate, endDate)
				})
			}));
	}

	// function parseData(array) {
	// 	// Trim zeros from the start and end
	// 	const trimmedArray = trimZeros(array);
	//
	// 	return trimmedArray.reduce((acc, item) => {
	// 		const formattedHour = moment(item.hour, "H").format("hA"); // Format hour using moment
	// 		acc[formattedHour] = parseFloat(item.total); // Ensure numeric values for totals
	// 		return acc;
	// 	}, {});
	// }
	//
	// function trimZeros(array) {
	// 	// Find the first index that isn't zero
	// 	const startIndex = array.findIndex(item => parseFloat(item.total) !== 0);
	// 	// Find the last index that isn't zero
	// 	const endIndex = array.length - 1 - array.slice().reverse().findIndex(item => parseFloat(item.total) !== 0);
	//
	// 	// Slice the array to keep only the relevant portion
	// 	return array.slice(startIndex, endIndex + 1);
	// }


	// function formatData(chartData) {
	// 	setPhoneLogsData(parseData(chartData.phone_logs))
	// 	setHiringNotesData(parseData(chartData.hiring_notes))
	// 	setHelpdeskNotesData(parseData(chartData.helpdesk_notes))
	// }


	return (
		<div className={'row'} style={{display: 'flex', flexDirection: 'row', rowGap: 25}} key={data.length + filterPeriod}>
			{data.map(({user_data, total_numbers, feedbacks, refunds, refunds_dollars, revisits, work_orders, work_order_dollars, revenue, tors}) => <div
					className={'col-12'}>
					<UserCard user={user_data[0]} total_numbers={total_numbers} filterPeriod={filterPeriod} feedbacks={feedbacks}
					          refunds={refunds} refunds_dollars={refunds_dollars} revenue={revenue}
					          revisits={revisits} work_orders={work_orders} work_order_dollars={work_order_dollars} tors={tors}
					/>
				</div>
			)}
		</div>
	);

};

const styles = {
	wrapper: {
		display: "flex",
		flexDirection: "column",
		gap: "16px", // Space between toolbar and charts
		padding: "16px", // Outer padding
	},
	chartTitle: {
		textAlign: "center",
		marginBottom: 0,
		fontSize: "18px",
		fontWeight: "bold",
		marginTop: 0
	},
	labelText: {
		fontSize: "1.2em",
	},
	chart: {
		minWidth: 300,
		maxWidth: 300,
		minHeight: 300,
		maxHeight: 300
	},
};

const UserCard = ({user, total_numbers, filterPeriod, feedbacks, refunds, refunds_dollars, revisits, work_order_dollars, work_orders, revenue, tors}) => {
	return (
		<Card style={{paddingBlock: 40}}>
			<h3 style={{
				textAlign: "center",
				marginBottom: 0,
				fontSize: "22px",
				fontWeight: "bold",
				marginTop: 0
			}}>{user.name_first + ' ' + user.name_last}</h3>
			<div className={'row'}>
				{/* Static Stats */}
				<div className={'col-3'} style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
					{/*<h3 style={styles.chartTitle}>AVG Per {filterPeriod === '7 days' ? 'Day' : 'Week'}</h3>*/}
					<div className={'row'} style={{flexDirection: "row"}}>
						<div className={'col-6'}>
							<TextField
								value={total_numbers.work_orders}
								label={'Total WOs'}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
						<div className={'col-6'}>
							<TextField
								value={(total_numbers.work_orders / (filterPeriod === '7 days' ? 7 : (filterPeriod.split(' ')[0] / 7))).toFixed(2)}
								label={`WOs/${filterPeriod === '7 days' ? 'Day' : 'Week'}`}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
					</div>
					<div className={'row'} style={{flexDirection: "row"}}>
						<div className={'col-6'}>
							<TextField
								value={(total_numbers.revenue / (total_numbers.work_orders > 0 ? total_numbers.work_orders : 1)).toFixed(2)}
								label={'AVG$/WO'}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
						<div className={'col-6'}>
							<TextField
								value={total_numbers.feedback}
								label={'Total Feedbacks'}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
					</div>
					<div className={'row'} style={{flexDirection: "row"}}>
						<div className={'col-6'}>
							<TextField
								value={(total_numbers.revenue / (filterPeriod === '7 days' ? 7 : (filterPeriod.split(' ')[0] / 7))).toFixed(2)}
								label={`Revenue/${filterPeriod === '7 days' ? 'Day' : 'Week'}`}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
						<div className={'col-6'}>
							<TextField
								value={total_numbers.revenue}
								label={'Total Revenue'}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
					</div>
					<div className={'row'} style={{flexDirection: "row"}}>
						<div className={'col-6'}>
							<Tooltip
								title="Count of all statuses, Unapproved and Denied not shown (but are counted here) as they should not sit in this status."
								arrow>
								<TextField
									value={total_numbers.total_tors}
									label={'Total TORs'}
									disabled={true}
									fullWidth={true}
									margin={'dense'}
									variant={'outlined'}
								/>
							</Tooltip>
						</div>
						<div className={'col-6'}>
							<Tooltip title="TORs Approved by a TOR manager or auto approved (2 weeks out when created)." arrow>
								<TextField
									value={total_numbers.approved_tors}
									label={'Approved TORs'}
									disabled={true}
									fullWidth={true}
									margin={'dense'}
									variant={'outlined'}
								/>
							</Tooltip>
						</div>
					</div>
					<div className={'row'} style={{flexDirection: "row"}}>
						<div className={'col-6'}>
							<Tooltip title="TORs that were Denied by a TOR manager, then FE responded with Not Showing Up (Unapproved Absence)." arrow>
								<TextField
									value={total_numbers.unapproved_tors}
									label={'Unapproved TORs'}
									disabled={true}
									fullWidth={true}
									margin={'dense'}
									variant={'outlined'}
								/>
							</Tooltip>
						</div>
						<div className={'col-6'}>
							<Tooltip title="TORs that were Denied by a TOR manager, then FE responded with Will Show Up (Will Work)." arrow>
								<TextField
									value={total_numbers.cancelled_tors}
									label={'Cancelled TORs'}
									disabled={true}
									fullWidth={true}
									margin={'dense'}
									variant={'outlined'}
								/>
							</Tooltip>
						</div>
					</div>
					<div className={'row'} style={{flexDirection: "row"}}>
						<div className={'col-6'}>
							<TextField
								value={total_numbers.revisits}
								label={'Total Revisits'}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
						<div className={'col-6'}>
							<TextField
								value={(total_numbers.revisits / (filterPeriod === '7 days' ? 7 : (filterPeriod.split(' ')[0] / 7))).toFixed(2)}
								label={`Revisits/${filterPeriod === '7 days' ? 'Day' : 'Week'}`}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
					</div>
					<div className={'row'} style={{flexDirection: "row"}}>
						<div className={'col-6'}>
							<TextField
								value={total_numbers.refunds}
								label={'Total Refunds'}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
						<div className={'col-6'}>
							<TextField
								value={total_numbers.refunds_dollar}
								label={'Total $ of Refunds'}
								disabled={true}
								fullWidth={true}
								margin={'dense'}
								variant={'outlined'}
							/>
						</div>
					</div>
				</div>

				<div className={'col-9'} style={{
					display: "flex",
					flexWrap: "wrap",
					rowGap: 35,
					justifyContent: "space-between"
				}}>
					<div style={styles.chart}>
						<h3 style={styles.chartTitle}>WOs</h3>
						<LineChart data={work_orders}/>
					</div>
					<div style={styles.chart}>
						<h3 style={styles.chartTitle}>AVG WO$</h3>
						<LineChart data={work_order_dollars}/>
					</div>
					<div style={styles.chart}>
						<h3 style={styles.chartTitle}>Revenue</h3>
						<LineChart data={revenue}/>
					</div>
					<div style={styles.chart}>
						<h3 style={styles.chartTitle}>TORs</h3>
						<LineChart data={tors}/>
					</div>
					<div style={styles.chart}>
						<h3 style={styles.chartTitle}>Feedback</h3>
						<LineChart data={feedbacks}/>
					</div>
					<div style={styles.chart}>
						<h3 style={styles.chartTitle}>Revisits</h3>
						<LineChart data={revisits}/>
					</div>
					<div style={styles.chart}>
						<h3 style={styles.chartTitle}>Refunds #</h3>
						<LineChart data={refunds}/>
					</div>
					<div style={styles.chart}>
						<h3 style={styles.chartTitle}>Refunds $</h3>
						<LineChart data={refunds_dollars}/>
					</div>
				</div>
			</div>
		</Card>
	)
}

export default ExecutiveCharts;